import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
  type CanActivateFn,
} from '@angular/router';
import { lazyProvider } from '@ngxs/store';
import { firstValueFrom } from 'rxjs';

import type { TypedRoute } from '@cosmos/router/types';
import {
  CosmosTranslocoService,
  LanguageScope,
} from '@cosmos/util-translations';
import {
  customerPortalAuthGuard,
  customerPortalPresentationAuthGuard,
  customerPortalPresentationPrintAuthGuard,
} from '@customer-portal/auth/data-access-customer-portal-access-guard';
import { PagesToTrackVisit } from '@customer-portal/common/data-access-visit-notification';
import { documentDetailsRoutes } from '@customer-portal/documents/feature-shell-web';
import {
  paymentProvidersSettingsResolver,
  paypalPaymentResolver,
} from '@customer-portal/invoices/feature-invoice-payment';

import { CustomerPortalProjectsHostComponent } from './projects-host/projects-host.component';
import {
  resolvePresentation,
  resolvePresentationPrint,
  resolveProjectDetails,
  resolveTemplateVariables,
} from './resolvers';

function getTitle(key: string) {
  return `${LanguageScope.CustomerPortalCommon}.route-titles.${key}`;
}

/**
 * Guard that redirects old project-scoped presentation URLs to the new stable
 * canonical presentation URL, preserving sub-paths and query params.
 *
 * `/projects/:projectId/presentations/:presentationId/products?foo=bar`
 *   → `/presentations/:presentationId/products?foo=bar`
 */
const redirectPresentationToCanonicalUrl: CanActivateFn = (
  _route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const router = inject(Router);
  const newUrl = state.url.replace(
    /^\/projects\/[^/]+\/presentations/,
    '/presentations'
  );
  return router.parseUrl(newUrl);
};

export const routes: TypedRoute<{ visitedPage?: PagesToTrackVisit }>[] = [
  // Backward-compat redirect: old project-scoped presentation URLs → stable canonical URL.
  // Must appear BEFORE the general /projects/:projectId route so Angular matches it first.
  {
    path: 'projects/:projectId/presentations/:presentationId',
    canActivate: [redirectPresentationToCanonicalUrl],
    children: [
      {
        path: '**',
        children: [],
      },
    ],
  },

  // Standalone presentation print page — kept as a top-level route so it loads
  // only the data it needs without the full project/presentation tree.
  {
    path: 'presentations/:presentationId/print',
    pathMatch: 'full',
    data: {
      meta: { title: getTitle('presentation-print') },
    },
    canActivate: [
      () => {
        // Preload guard: Triggers component download in parallel with auth/resolvers
        // This doesn't await the import, so it starts fetching immediately but
        // doesn't block route activation. By the time guards + resolvers finish,
        // the component JS is likely already downloaded.
        import(
          '@customer-portal/presentations/feature-presentation-products-print-preview'
        );
        return true; // Always pass - this guard only triggers the preload.
      },
      () => {
        // Preload translations in parallel with component download and auth
        // Fetches all required translation scopes without blocking route activation
        // If component needs translations immediately on render,
        // they'll likely be cached already.
        const scopesToPreload = [
          LanguageScope.Cosmos,
          LanguageScope.EspCommon,
          LanguageScope.CustomerPortalCommon,
        ];

        const translocoService = inject(CosmosTranslocoService);

        // Fire-and-forget: start loading but don't wait.
        firstValueFrom(
          translocoService.ensureScopesAreLoaded$(scopesToPreload),
          { defaultValue: undefined }
        );

        return true; // Always pass - this guard only triggers the preload.
      },
      customerPortalPresentationPrintAuthGuard,
    ],
    resolve: [resolvePresentationPrint, resolveTemplateVariables],
    loadComponent: async () =>
      // When Angular reaches loadComponent, the import is already in-flight or cached
      // So this await typically resolves instantly (already downloaded) instead of starting a new network request
      (
        await import(
          '@customer-portal/presentations/feature-presentation-products-print-preview'
        )
      ).PresentationPrintPreviewComponent,
  },

  // Common parent route for all project and presentation (project based and standalone) routes.
  // We reuse the same host because:
  // - it has the same layout (footer, header etc)
  // - we want to apply presentation template styles (if available) in both cases
  {
    path: '',
    component: CustomerPortalProjectsHostComponent,
    resolve: [resolveTemplateVariables],
    children: [
      {
        path: 'projects/:projectId',
        canActivate: [customerPortalAuthGuard],
        resolve: [resolveProjectDetails],
        children: [
          {
            path: 'proofs',
            loadChildren: () =>
              import('@customer-portal/proofs/feature-proofs'),
            data: {
              visitedPage: PagesToTrackVisit.Proofs,
              meta: {
                title: getTitle('proofs'),
              },
            },
          },
          {
            path: 'orders',
            pathMatch: 'full',
            loadComponent: async () =>
              (await import('@customer-portal/orders/feature-orders-list'))
                .CustomerPortalOrdersListPage,
            canActivate: [
              lazyProvider(
                () => import('@customer-portal/orders/data-access/store')
              ),
            ],
            data: {
              meta: {
                title: getTitle('orders'),
              },
            },
          },
          {
            path: 'quotes',
            pathMatch: 'full',
            loadComponent: async () =>
              (await import('@customer-portal/quotes/feature-quotes-list'))
                .CustomerPortalQuotesListPage,
            canActivate: [
              lazyProvider(
                () => import('@customer-portal/quotes/data-access/store')
              ),
            ],
            data: {
              meta: {
                title: getTitle('quotes'),
              },
            },
          },
          {
            path: 'invoices',
            pathMatch: 'full',
            loadChildren: () =>
              import('@customer-portal/invoices/feature-shell-web'),
            resolve: [paymentProvidersSettingsResolver, paypalPaymentResolver],
            data: {
              meta: {
                title: getTitle('invoices'),
              },
            },
          },
          ...documentDetailsRoutes,
          {
            path: 'asset-not-found',
            loadComponent: async () =>
              (
                await import(
                  /* webpackChunkName: 'ui-asset-not-found' */ '@customer-portal/common/ui-asset-not-found'
                )
              ).CustomerPortalAssetNotFoundComponent,
            data: {
              meta: { title: getTitle('asset-not-found') },
            },
          },
        ],
      },

      // Stable canonical presentation URL.
      // Handles both standalone presentations (no ProjectId) and project-linked presentations.
      {
        path: 'presentations/:presentationId',
        canActivate: [customerPortalPresentationAuthGuard],
        resolve: [resolvePresentation],
        data: {
          visitedPage: PagesToTrackVisit.Products,
          meta: { title: getTitle('presentation') },
        },
        loadChildren: async () =>
          (await import('@customer-portal/presentations/feature-presentations'))
            .routes,
      },

      // There is no sense to activate host component if there is no child route to activate,
      // so we redirect to not-found in this case
      {
        path: '',
        pathMatch: 'full',
        redirectTo: '/not-found',
      },
    ],
  },

  // standalone documents
  {
    path: '',
    loadComponent: async () =>
      (await import('@customer-portal/documents/feature-layout-standalone'))
        .StandaloneDocumentLayoutComponent,
    children: [
      ...documentDetailsRoutes,
      {
        path: '',
        pathMatch: 'full',
        redirectTo: '/not-found', // or load a NotFoundComponent
      },
    ],
  },
  {
    path: 'collections',
    loadChildren: async () =>
      (
        await import(
          /* webpackChunkName: 'feature-collection' */ '@customer-portal/collections/feature-collection'
        )
      ).customerPortalCollectionRoutes,
    data: {
      meta: {
        title: getTitle('collections'),
      },
    },
  },
  {
    path: 'products',
    loadChildren: async () =>
      (await import('@customer-portal/collections/feature-product-details'))
        .customerPortalProductRoutes,
    data: {
      meta: {
        title: getTitle('products'),
      },
    },
  },

  // unauthorized error page
  {
    path: 'unauthorized',
    loadComponent: async () =>
      (await import('@customer-portal/common/ui-error-page'))
        .CustomerPortalUnauthorizedErrorPage,
    data: {
      meta: { title: getTitle('unauthorized') },
    },
  },
  // general error page
  {
    path: 'error',
    loadComponent: async () =>
      (await import('@customer-portal/common/ui-error-page'))
        .CustomerPortalErrorPage,
    data: {
      meta: { title: getTitle('error') },
    },
  },
  // not found error page
  {
    path: '**',
    loadComponent: async () =>
      (await import('@customer-portal/common/ui-error-page'))
        .CustomerPortalNotFoundErrorPage,
    data: {
      meta: {
        title: getTitle('not-found'),
        robots: 'noindex, nofollow',
      },
    },
  },
];
